// Cause & Effect rule model — pure helpers, no React and no network.
//
// Mirrors the rule document defined in SPEC_AUTOMATION_CAUSE_EFFECT.md §4-§5. Everything here is
// deliberately side-effect free so the shape, the validation and the sentence rendering can be
// reasoned about (and unit tested) without a backend or a browser.
//
// FIRST PRINCIPLE (spec §2.1): nothing in this file ever invents a rule, seeds a default, or
// infers a device from a register name. `emptyRule()` returns a draft with every device
// reference blank, and `duplicateRule()` strips them. If you find yourself adding a
// DEFAULT_RULES array here, re-read the spec.

export const SAFETY_CLASSES = [
  {
    value: "supervisory",
    label: "Supervisory",
    blurb: "Never writes. Raises alerts only — including when hardwired panel logic should have acted and did not.",
    tone: "amber",
  },
  {
    value: "control",
    label: "Control",
    blurb: "Actually writes the relay. Requires a feedback point and passes every safety rail.",
    tone: "red",
  },
  {
    value: "control_backup",
    label: "Control (backup to panel)",
    blurb: "Writes, but panel logic is authoritative. Same engine behaviour as Control.",
    tone: "emerald",
  },
];

export const MODES = [
  { value: "disabled", label: "Disabled", blurb: "Not evaluated at all.", tone: "gray" },
  { value: "simulate", label: "Simulate", blurb: "Fully evaluated and logged, but never acts.", tone: "sky" },
  { value: "armed",    label: "Armed",    blurb: "Live. Actions are performed.", tone: "red" },
];

// Fixed condition grammar — spec §2.2. There is deliberately no expression string and no
// user-supplied code anywhere in a rule.
export const OPERATORS = [
  { value: "lt",         label: "is below",        analog: true,  needsValue: true },
  { value: "lte",        label: "is at or below",  analog: true,  needsValue: true },
  { value: "gt",         label: "is above",        analog: true,  needsValue: true },
  { value: "gte",        label: "is at or above",  analog: true,  needsValue: true },
  { value: "eq",         label: "is",              analog: false, needsValue: true },
  { value: "neq",        label: "is not",          analog: false, needsValue: true },
  { value: "changed_to", label: "changes to",      analog: false, needsValue: true },
  { value: "offline_for", label: "has been offline for", analog: false, needsValue: true, unit: "s" },
];

export const RESET_MODES = [
  { value: "auto_on_clear", label: "automatically, when the condition clears" },
  { value: "manual",        label: "only when an operator resets it" },
];

export const ON_FAIL = [
  { value: "critical_alert", label: "raise a critical alert" },
  { value: "warning_alert",  label: "raise a warning alert" },
  { value: "revert",         label: "revert the output and alert" },
];

export const ALERT_SEVERITIES = ["info", "warning", "critical"];

// The three writable signal types — the same list ControlManagement.jsx filters on. A rule may
// only target an IO point of one of these kinds.
export const OUTPUT_SIGNAL_TYPES = ["digital_output", "binary_output", "analog_output"];

export const ANALOG_SIGNAL_TYPES = ["analog_input", "analog_output", "ai", "ao"];

// Secondary registers an asset can carry, matching EXTRA_META_KEYS in backend/asset_registry.py.
// `run_status` is the one addition this feature needs — see SPEC_AUTOMATION_BACKEND_CHANGES.md.
export const CHANNEL_META_KEYS = {
  direction:    "direction_io_point_name",
  door_status:  "door_io_point_name",
  motor_temp:   "motor_temp_io_point_name",
  pump_trip:    "pump_trip_io_point_name",
  auto_manual:  "auto_manual_io_point_name",
  power_status: "power_status_io_point_name",
  run_status:   "run_status_io_point_name",
};

// The one WRITE channel — the relay that actuates a piece of equipment, as opposed to the
// read-only registers above that report on it. Deliberately separate from CHANNEL_META_KEYS:
// availableChannels() drives what a TRIGGER may watch, and a control relay is only ever an
// output. Mirrors CONTROL_META_KEY in backend/asset_registry.py.
//
// Without this, a pump whose primary link is its running-status DI could never be picked as a
// rule's output at all — only the raw, generically-named relay coil could, which is what made
// "Hydrant Jockey Pump" impossible to select under Actions.
export const CONTROL_META_KEY = "control_io_point_name";

export const CHANNEL_LABELS = {
  primary:      "primary reading",
  direction:    "direction",
  door_status:  "door status",
  motor_temp:   "motor temperature",
  pump_trip:    "trip signal",
  auto_manual:  "auto / manual",
  power_status: "power status",
  run_status:   "run status",
  control:      "control relay",
};

// ── Draft construction ────────────────────────────────────────────────────────

export function emptyCondition() {
  return { source: null, op: "lt", value: "", unit: "", for_seconds: "" };
}

export function emptyRule(siteId) {
  return {
    site_id: siteId || "",
    name: "",
    description: "",
    // Spec §3: a new rule is supervisory and disabled. Promotion is a separate, deliberate act.
    safety_class: "supervisory",
    mode: "disabled",
    priority: 100,
    trigger: { all: [emptyCondition()] },
    permissives: [],
    actions: [],
    feedback: null,
    reset: { mode: "auto_on_clear", hysteresis: "", min_off_seconds: "" },
    limits: { max_actuations_per_hour: 6, cooldown_seconds: 60 },
    on_comms_loss: "hold_and_alert",
    is_active: true,
  };
}

// Copying a rule to another site keeps the logic and drops every device reference (spec §2.1).
export function duplicateRule(rule, siteId) {
  const stripSource = (c) => ({ ...c, source: null });
  return {
    ...emptyRule(siteId),
    name: rule.name ? `${rule.name} (copy)` : "",
    description: rule.description || "",
    safety_class: rule.safety_class || "supervisory",
    priority: rule.priority ?? 100,
    trigger: { all: (rule.trigger?.all || [emptyCondition()]).map(stripSource) },
    permissives: (rule.permissives || []).map(stripSource),
    actions: (rule.actions || []).map((a) => ({ ...a, target: null })),
    feedback: rule.feedback ? { ...rule.feedback, source: null } : null,
    reset: { ...(rule.reset || {}) },
    limits: { ...(rule.limits || {}) },
  };
}

// ── Source helpers ────────────────────────────────────────────────────────────

export function sourceKey(source) {
  if (!source) return "";
  if (source.kind === "asset") return `asset:${source.asset_id}:${source.channel || "primary"}`;
  if (source.kind === "io") return `io:${source.gateway_id}:${source.io_point_name}`;
  if (source.kind === "gateway") return `gateway:${source.gateway_id}:${source.property || "online"}`;
  return "";
}

// Which channels an asset has actually linked. A channel the asset has not wired is never
// offered — the engineer picks from what exists, never from a list of what might exist.
export function availableChannels(asset) {
  if (!asset) return [];
  const out = [];
  if (asset.io_point_name) out.push("primary");
  const meta = asset.meta || {};
  for (const [channel, metaKey] of Object.entries(CHANNEL_META_KEYS)) {
    if (meta[metaKey]) out.push(channel);
  }
  return out;
}

export function isAnalogOperator(op) {
  return OPERATORS.find((o) => o.value === op)?.analog === true;
}

export function isWritableSignalType(signalType) {
  return OUTPUT_SIGNAL_TYPES.includes(signalType);
}

// ── Validation — mirrors the save-time rejections in spec §9 ──────────────────
//
// Deliberately strict, and deliberately at edit time rather than at fire time. A
// half-configured rule that only fails when it tries to start a pump during an incident is far
// worse than one that refuses to save. Same reasoning as _validate_write_addressing() in uhal.py.

export function validateRule(rule, ctx = {}) {
  const { assetsById = {}, ioBySourceKey = {} } = ctx;
  const errors = [];
  const add = (field, message) => errors.push({ field, message });

  if (!rule.name || !rule.name.trim()) add("name", "Give the rule a name.");
  if (!rule.site_id) add("site_id", "A rule belongs to exactly one site.");

  const conditions = rule.trigger?.all || [];
  if (!conditions.length) add("trigger", "A rule needs at least one trigger condition.");

  const checkCondition = (c, field, { allowDelay }) => {
    if (!c.source) {
      add(field, "Pick the device this condition watches.");
      return;
    }
    if (c.source.kind === "asset") {
      const asset = assetsById[c.source.asset_id];
      if (!asset) {
        add(field, "That device is no longer at this site.");
      } else {
        if (asset.site_id && rule.site_id && asset.site_id !== rule.site_id) {
          add(field, `"${asset.name}" belongs to a different site.`);
        }
        const channels = availableChannels(asset);
        const channel = c.source.channel || "primary";
        if (!channels.includes(channel)) {
          add(field, `"${asset.name}" has no ${CHANNEL_LABELS[channel] || channel} point linked yet.`);
        }
      }
    }
    if (c.value === "" || c.value === null || c.value === undefined) {
      add(field, "Set the value to compare against.");
    }
    if (allowDelay && isAnalogOperator(c.op)) {
      // Spec §5 — mandatory on every analog condition, no exceptions.
      if (!c.for_seconds || Number(c.for_seconds) <= 0) {
        add(field, "An analog condition must hold for a set time before it fires. Set a delay.");
      }
    }
  };

  conditions.forEach((c, i) => checkCondition(c, `trigger.${i}`, { allowDelay: true }));
  // A permissive is a gate checked at the moment of firing — a delay on it would be meaningless.
  (rule.permissives || []).forEach((c, i) => checkCondition(c, `permissives.${i}`, { allowDelay: false }));

  const actions = rule.actions || [];
  if (!actions.length) add("actions", "A rule that does nothing is not a rule. Add an action.");

  const writes = actions.filter((a) => a.type === "write");
  if (rule.safety_class === "supervisory" && writes.length) {
    add("actions", "A supervisory rule never writes. Change the class, or remove the write action.");
  }

  writes.forEach((a, i) => {
    if (!a.target) {
      add(`actions.${i}`, "Pick the output this action drives.");
      return;
    }
    const io = ioBySourceKey[sourceKey(a.target)];
    if (io && !isWritableSignalType(io.signal_type)) {
      add(`actions.${i}`, `"${io.label}" is an input (${io.signal_type}) — it cannot be written.`);
    }
    // An asset target resolves through its control channel, which lives on the asset rather than
    // in the IO index — so the check above (keyed on io: sources) can never see it.
    if (a.target.kind === "asset") {
      const asset = assetsById[a.target.asset_id];
      if (!asset) {
        add(`actions.${i}`, "That device is no longer at this site.");
      } else if ((a.target.channel || "primary") === "control" && !(asset.meta || {})[CONTROL_META_KEY]) {
        add(`actions.${i}`, `"${asset.name}" has no control relay linked yet. Set its Control Relay Register in the Asset Registry.`);
      }
    }
    if (a.value === "" || a.value === null || a.value === undefined) {
      add(`actions.${i}`, "Set the value to write.");
    }
  });

  // Spec §7 rail 8 — a control rule with no way to confirm it worked is not acceptable.
  if (rule.safety_class !== "supervisory" && writes.length) {
    if (!rule.feedback || !rule.feedback.source) {
      add("feedback", "A rule that writes must confirm it took effect. Pick a feedback point.");
    } else if (!rule.feedback.timeout_s || Number(rule.feedback.timeout_s) <= 0) {
      add("feedback", "Set how long to wait for confirmation.");
    }
  }

  // Spec §7 rail 4 — start at 50%, stop at 60%; never both at 50%.
  if (rule.reset?.mode === "auto_on_clear") {
    const h = Number(rule.reset.hysteresis);
    if (!h || h <= 0) {
      add("reset", "Auto-reset needs a hysteresis band, or the output will chatter around the setpoint.");
    }
  }

  return errors;
}

export function errorsFor(errors, field) {
  return errors.filter((e) => e.field === field).map((e) => e.message);
}

// ── Sentence rendering ────────────────────────────────────────────────────────
//
// The same phrasing the builder uses, as plain text — for list rows, tooltips, the matrix cell
// title, and anywhere a rule needs to be readable without opening it.

export function describeSource(source, ctx = {}) {
  if (!source) return "(not set)";
  const { assetsById = {}, ioBySourceKey = {} } = ctx;
  if (source.kind === "asset") {
    const asset = assetsById[source.asset_id];
    const name = asset?.name || source.asset_id;
    const channel = source.channel && source.channel !== "primary"
      ? ` ${CHANNEL_LABELS[source.channel] || source.channel}`
      : "";
    return `${name}${channel}`;
  }
  if (source.kind === "io") {
    return ioBySourceKey[sourceKey(source)]?.label || source.io_point_name || "(register)";
  }
  if (source.kind === "gateway") return "gateway connection";
  return "(not set)";
}

function describeValue(c) {
  if (c.value === "" || c.value === null || c.value === undefined) return "…";
  if (c.unit) return `${c.value} ${c.unit}`;
  if (c.value === "1" || c.value === 1) return "ON";
  if (c.value === "0" || c.value === 0) return "OFF";
  return String(c.value);
}

export function describeCondition(c, ctx) {
  const op = OPERATORS.find((o) => o.value === c.op)?.label || c.op;
  const delay = c.for_seconds ? ` for ${c.for_seconds}s` : "";
  return `${describeSource(c.source, ctx)} ${op} ${describeValue(c)}${delay}`;
}

export function describeAction(a, ctx) {
  if (a.type === "write") {
    const verb = String(a.value) === "1" ? "Start" : String(a.value) === "0" ? "Stop" : `Set to ${a.value}`;
    return `${verb} ${describeSource(a.target, ctx)}`;
  }
  if (a.type === "alert") return `Raise ${a.severity || "warning"} alert — ${a.title || "untitled"}`;
  return a.type;
}

export function describeRule(rule, ctx) {
  const parts = [];
  const conds = rule.trigger?.all || [];
  if (conds.length) parts.push(`When ${conds.map((c) => describeCondition(c, ctx)).join(" and ")}`);
  const perms = rule.permissives || [];
  if (perms.length) parts.push(`while ${perms.map((c) => describeCondition(c, ctx)).join(" and ")}`);
  const acts = rule.actions || [];
  if (acts.length) parts.push(`then ${acts.map((a) => describeAction(a, ctx)).join(", ")}`);
  if (rule.feedback?.source) {
    parts.push(`confirm via ${describeSource(rule.feedback.source, ctx)} within ${rule.feedback.timeout_s || "?"}s`);
  }
  return parts.join(", ") + ".";
}

// Every device a rule touches — used to show a rule on an asset's own Automation panel, and to
// find rules that would be orphaned when a device is deleted.
export function referencedAssetIds(rule) {
  const ids = new Set();
  const walk = (src) => { if (src?.kind === "asset" && src.asset_id) ids.add(src.asset_id); };
  (rule.trigger?.all || []).forEach((c) => walk(c.source));
  (rule.permissives || []).forEach((c) => walk(c.source));
  (rule.actions || []).forEach((a) => walk(a.target));
  walk(rule.feedback?.source);
  return Array.from(ids);
}

// Which side of the matrix a device sits on for a given rule.
export function assetRoleInRule(rule, assetId) {
  const inList = (list, key) => (list || []).some((x) => x[key]?.kind === "asset" && x[key]?.asset_id === assetId);
  if (inList(rule.actions, "target")) return "output";
  if (inList(rule.trigger?.all, "source")) return "input";
  if (inList(rule.permissives, "source")) return "permissive";
  if (rule.feedback?.source?.kind === "asset" && rule.feedback.source.asset_id === assetId) return "feedback";
  return null;
}
